import { TagGroupWithTags } from "apps/adminland/directories/tagManager/useTagManager";
import {
  EnumOwnerType,
  EnumTagEntityTypes,
  EnumTagGroupKey,
  EnumTagGroupType,
  TagByIdQuery,
  TagGroup,
  TagManyWithCountsQuery,
  useMediaGalleryTagGroupQuery,
  useTagManyWithCountsQuery,
} from "gql/generated";
import _ from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { useFeatureFlag } from "shared/contextProviders/featureFlags";
import { useTagGroupContext } from "shared/contextProviders/tagGroups";
import { FeatureFlags } from "shared/featureFlags";
import { getMultilingualString } from "shared/utils";

export type EntityFilterKind =
  | "listing"
  | "event"
  | "promotion"
  | "form"
  | "media";

const ENTITY_TYPE_BY_FILTER: Record<
  EntityFilterKind,
  (typeof EnumTagEntityTypes)[keyof typeof EnumTagEntityTypes]
> = {
  listing: EnumTagEntityTypes.Operator,
  event: EnumTagEntityTypes.Event,
  promotion: EnumTagEntityTypes.Promotion,
  form: EnumTagEntityTypes.Person,
  media: EnumTagEntityTypes.Organization,
};

export const useCustomTags = () => {
  const { organizationId } = useParams();
  const mediaGalleryEnabled = useFeatureFlag(FeatureFlags.MediaGalleryWidget);

  const {
    loading: tagGroupLoading,
    error: tagGroupError,
    getTagGroup,
  } = useTagGroupContext();
  const tagGroupData = useMemo(
    () =>
      getTagGroup({
        key: EnumTagGroupKey.BackOffice,
        type: EnumTagGroupType.OrganizationSpecific,
      }),
    [getTagGroup],
  );

  const {
    data: mediaGalleryGroupData,
    loading: mediaGalleryGroupLoading,
    error: mediaGalleryGroupError,
  } = useMediaGalleryTagGroupQuery({
    skip: !mediaGalleryEnabled,
  });

  const mediaGalleryTagGroup = useMemo(() => {
    const group = mediaGalleryGroupData?.mediaGalleryTagGroup as
      | TagGroup
      | null
      | undefined;
    if (!group?._id) return undefined;
    return group;
  }, [mediaGalleryGroupData?.mediaGalleryTagGroup]);

  // The options for the category dropdown in the flyouts
  const flyoutCategoryOptions = useMemo(() => {
    const options: {
      value: string;
      label: string;
      key: EnumTagGroupKey;
      type: EnumTagGroupType;
    }[] = [];

    if (tagGroupData) {
      options.push({
        value: tagGroupData._id,
        label: getMultilingualString(tagGroupData.name),
        key: tagGroupData.key,
        type: tagGroupData.type,
      });
    }

    if (mediaGalleryEnabled && mediaGalleryTagGroup) {
      options.push({
        value: mediaGalleryTagGroup._id,
        label: getMultilingualString(mediaGalleryTagGroup.name),
        key: mediaGalleryTagGroup.key,
        type: mediaGalleryTagGroup.type,
      });
    }

    return options;
  }, [tagGroupData, mediaGalleryEnabled, mediaGalleryTagGroup]);

  const {
    data: tagData,
    loading: tagLoading,
    error: tagError,
  } = useTagManyWithCountsQuery({
    variables: {
      filter: {
        tagGroup: tagGroupData?._id ? [tagGroupData._id] : null,
        owner: { entityId: organizationId, type: EnumOwnerType.Organization },
        entityTypes: [
          EnumTagEntityTypes.Operator,
          EnumTagEntityTypes.Event,
          EnumTagEntityTypes.Promotion,
          EnumTagEntityTypes.Person,
        ],
      },
      limit: 10000,
    },
    skip: !tagGroupData?._id || !organizationId,
  });

  const {
    data: mediaTagData,
    loading: mediaTagLoading,
    error: mediaTagError,
  } = useTagManyWithCountsQuery({
    variables: {
      filter: {
        tagGroup: mediaGalleryTagGroup?._id ? [mediaGalleryTagGroup._id] : null,
        owner: { entityId: organizationId, type: EnumOwnerType.Organization },
        entityTypes: [EnumTagEntityTypes.Organization],
      },
      limit: 10000,
    },
    skip: !mediaGalleryEnabled || !mediaGalleryTagGroup?._id || !organizationId,
  });

  const [allTagGroupsWithTags, setAllTagGroupsWithTags] = useState<
    TagGroupWithTags[]
  >([]);
  useEffect(() => {
    if (!tagGroupData || !tagData?.tagMany) return;

    const next: TagGroupWithTags[] = [
      {
        tagGroup: { ...tagGroupData },
        tags: _.orderBy(tagData.tagMany ?? [], "name.en", "asc"),
      },
    ];

    if (mediaGalleryEnabled && mediaGalleryTagGroup && mediaTagData?.tagMany) {
      next.push({
        tagGroup: { ...mediaGalleryTagGroup },
        tags: _.orderBy(mediaTagData.tagMany ?? [], "name.en", "asc"),
      });
    }

    setAllTagGroupsWithTags(next);
  }, [
    tagData,
    tagGroupData,
    mediaGalleryEnabled,
    mediaGalleryTagGroup,
    mediaTagData,
  ]);

  const [editingTag, setEditingTag] = useState<
    TagManyWithCountsQuery["tagMany"][0] | null
  >(null);

  /**
   * When a tag is updated, the tag will be updated in the appropriate tag group
   * @param tag - The tag to update
   * @returns
   */
  const handleTagUpdated = (tag: TagByIdQuery["tagById"]) => {
    if (!tag) return;

    setAllTagGroupsWithTags(prev =>
      prev.map(tagGroupWithTags =>
        tagGroupWithTags.tagGroup._id === tag.tagGroup?._id
          ? {
              ...tagGroupWithTags,
              tags: _.orderBy(
                tagGroupWithTags.tags.map(t =>
                  t._id === tag._id ? { ...tag } : t,
                ),
                "name.en",
                "asc",
              ) as TagManyWithCountsQuery["tagMany"],
            }
          : tagGroupWithTags,
      ),
    );
  };

  /**
   * When a tag is deleted, the tag will be removed from the appropriate tag group
   * @param tag - The tag to delete
   * @returns
   */
  const handleTagDeleted = (tag: TagByIdQuery["tagById"]) => {
    if (!tag) return;

    setAllTagGroupsWithTags(prev =>
      prev.map(tagGroupWithTags =>
        tagGroupWithTags.tagGroup._id === tag.tagGroup?._id
          ? {
              ...tagGroupWithTags,
              tags: tagGroupWithTags.tags.filter(
                t => t._id !== tag._id,
              ) as TagManyWithCountsQuery["tagMany"],
            }
          : tagGroupWithTags,
      ),
    );
  };

  /**
   * When a tag is split, the tag will be split into two new tags
   * @param tag - The tag to split
   * @param newTags - The new tags
   * @returns
   */
  const handleTagSplitCompleted = (
    updatedTag: TagByIdQuery["tagById"],
    newTags: TagByIdQuery["tagById"][],
  ) => {
    if (!updatedTag || !newTags || newTags.length === 0) return;

    setAllTagGroupsWithTags(prev =>
      prev.map(tagGroupWithTags =>
        tagGroupWithTags.tagGroup._id === updatedTag.tagGroup?._id
          ? {
              ...tagGroupWithTags,
              tags: _.orderBy(
                [
                  ...tagGroupWithTags.tags.map(tag =>
                    tag._id === updatedTag._id ? updatedTag : tag,
                  ),
                  ...newTags,
                ],
                "name.en",
                "asc",
              ) as TagManyWithCountsQuery["tagMany"],
            }
          : tagGroupWithTags,
      ),
    );
  };

  const [isTagModalOpen, setIsTagModalOpen] = useState(false);

  const [selectedFilters, setSelectedFilters] = useState<EntityFilterKind[]>(
    [],
  );

  const toggleFilter = (kind: EntityFilterKind) => {
    setSelectedFilters(prev =>
      prev.includes(kind) ? prev.filter(k => k !== kind) : [...prev, kind],
    );
  };

  const allTags = useMemo(
    () =>
      _.orderBy(
        allTagGroupsWithTags.flatMap(
          group => group.tags as TagManyWithCountsQuery["tagMany"],
        ),
        "name.en",
        "asc",
      ),
    [allTagGroupsWithTags],
  );
  const tags = useMemo(() => {
    if (selectedFilters.length === 0) return allTags;
    return allTags.filter((tag): tag is TagManyWithCountsQuery["tagMany"][0] =>
      selectedFilters.every(
        kind =>
          tag.entityTypes?.some(t => t === ENTITY_TYPE_BY_FILTER[kind]) ??
          false,
      ),
    );
  }, [allTags, selectedFilters]);

  const showEntityFilters = useMemo(() => {
    const getTagIdsForFilter = (kind: EntityFilterKind) =>
      _.sortBy(
        allTags
          .filter(
            tag =>
              tag.entityTypes?.some(t => t === ENTITY_TYPE_BY_FILTER[kind]) ??
              false,
          )
          .map(t => t._id),
      );
    const listingTagIds = getTagIdsForFilter("listing");
    const eventTagIds = getTagIdsForFilter("event");
    const promotionTagIds = getTagIdsForFilter("promotion");
    const formTagIds = getTagIdsForFilter("form");
    const mediaTagIds = getTagIdsForFilter("media");
    const hasDistinctBackOffice =
      !_.isEqual(listingTagIds, eventTagIds) ||
      !_.isEqual(listingTagIds, promotionTagIds) ||
      !_.isEqual(listingTagIds, formTagIds);
    const hasMediaTags = mediaGalleryEnabled && mediaTagIds.length > 0;
    return hasDistinctBackOffice || hasMediaTags;
  }, [allTags, mediaGalleryEnabled]);

  const isEditingMediaTag =
    !!editingTag &&
    (editingTag.tagGroup?.key === EnumTagGroupKey.MediaGallery ||
      !!editingTag.entityTypes?.includes(EnumTagEntityTypes.Organization));

  return {
    tags,
    showEntityFilters,
    selectedFilters,
    toggleFilter,
    tagLoading:
      tagLoading ||
      (mediaGalleryEnabled && (mediaTagLoading || mediaGalleryGroupLoading)),
    tagError: tagError || mediaTagError,
    tagGroupData,
    mediaGalleryTagGroupId: mediaGalleryTagGroup?._id,
    tagGroupLoading:
      tagGroupLoading || (mediaGalleryEnabled && mediaGalleryGroupLoading),
    tagGroupError: tagGroupError || mediaGalleryGroupError,
    allTagGroupsWithTags,
    flyoutCategoryOptions,
    organizationId,
    editingTag,
    setEditingTag,
    isEditingMediaTag,
    onTagUpdated: handleTagUpdated,
    onTagDeleted: handleTagDeleted,
    onTagSplitCompleted: handleTagSplitCompleted,
    // When a tag is merged, the tag will be deleted
    onTagMergeCompleted: handleTagDeleted,
    isTagModalOpen,
    setIsTagModalOpen,
  };
};
